import React, { useCallback, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { CloudUpload as UploadIcon } from '@mui/icons-material';

type Variant = 'color' | 'size';

const FORMAT_CONFIG: Record<Variant, {
  header: string;
  examples: string[];
  required: string;
  optional: string;
}> = {
  color: {
    header: 'code,display,hex_code,url,family,synonyms',
    examples: [
      'BLK,Black,#000000,https://res.cloudinary.com/colorSwatch/black.png,Neutral,',
      'WHT,White,#FFFFFF,https://res.cloudinary.com/colorSwatch/white.png,Neutral,',
      'NVY,Navy,#000080,https://res.cloudinary.com/colorSwatch/navy.png,Blue,dark blue|midnight',
    ],
    required: 'code, display, hex_code, url, family',
    optional: 'synonyms',
  },
  size: {
    header: 'code,display,family,primary_value,primary_label,secondary_value,secondary_label,tertiary_value,tertiary_label,synonyms',
    examples: [
      'SM,Small,Standard,S,Size,,,,,,',
      'MED,Medium,Standard,M,Size,,,,,,',
      'W32L30,32x30,Standard,32,Waist,30,Length,,,,',
    ],
    required: 'code, display, family',
    optional: 'primary_value, primary_label, secondary_value, secondary_label, tertiary_value, tertiary_label, synonyms',
  },
};

interface ImportFacetMappingsDialogProps {
  open: boolean;
  onClose: () => void;
  onFileSelected: (file: File) => void;
  variant: Variant;
}

export const ImportFacetMappingsDialog: React.FC<ImportFacetMappingsDialogProps> = ({
  open,
  onClose,
  onFileSelected,
  variant,
}) => {
  const config = FORMAT_CONFIG[variant];
  const [dragOver, setDragOver] = useState(false);

  const handleFile = useCallback(
    (file: File) => {
      const name = file.name.toLowerCase();
      if (!name.endsWith('.csv') && !name.endsWith('.tsv')) {
        return;
      }
      onFileSelected(file);
    },
    [onFileSelected],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setDragOver(false);

      const file = event.dataTransfer.files?.[0];
      if (file) {
        handleFile(file);
      }
    },
    [handleFile],
  );

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(false);
  };

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFile(file);
    }
    event.target.value = '';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import Facet Mappings</DialogTitle>
      <DialogContent>
        {/* Drop zone */}
        <Box
          component="label"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px dashed',
            borderColor: dragOver ? 'primary.main' : 'divider',
            borderRadius: 1,
            p: 4,
            mb: 3,
            textAlign: 'center',
            cursor: 'pointer',
            bgcolor: dragOver ? 'action.hover' : undefined,
            '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
          }}
        >
          <UploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Drag &amp; drop CSV or TSV file here
          </Typography>
          <Typography variant="body2" color="text.secondary">
            or click to select file
          </Typography>
          <input
            type="file"
            hidden
            accept=".csv,.tsv"
            onChange={handleFileInput}
            aria-label="Select CSV or TSV file for import"
          />
        </Box>

        {/* Expected CSV Format */}
        <Box>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Expected CSV Format:
          </Typography>
          <Box
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              color: 'text.secondary',
              m: 0,
              mb: 2,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {config.header}
            {'\n'}
            {config.examples.join('\n')}
          </Box>

          <Typography variant="body2">
            <strong>Required fields:</strong>
            {` ${config.required}`}
          </Typography>
          <Typography variant="body2">
            <strong>Optional fields:</strong>
            {` ${config.optional}`}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
            Tip: Use pipe (|) to separate multiple values in synonyms and family fields (e.g., &quot;value1|value2|value3&quot;)
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
